import { Form, Input, DatePicker, InputNumber, Divider, Typography, Select, Button, Modal, Spin, Tooltip } from "antd";
import { CloseOutlined, SaveOutlined, EyeOutlined } from "@ant-design/icons";
import { FaBaby } from "react-icons/fa";
import { useState, useRef } from "react";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import BaseService from "../../../../service/base.service";
import notify from "../../../../components/common/notificationHelper";
import Examinations from "../../../../models/examination";
import ExaminationDetails from "../../../../models/examinationDetails";
import ObstetricExaminationPreviewModal from "./ObstetricExaminationPreviewModal"
import ObstetricComparisonModal from "./ObstetricComparisonModal";
import { DiffOutlined } from "@ant-design/icons";
import { useEffect } from "react";
import { useIsMobile } from "../../../../hooks/useIsMobile";

const { Title } = Typography;
const { Option } = Select;
const BabyIcon = FaBaby as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

const initialAnalyses = [
    "AZOT KREAT",
    "BILURUBINA TOTALE INDIREKTE DIREKTE",
    "AST/ALT",
    "ERITROSEDIMENT",
    "FIBRINOGJEN PCR",
    "VIT D 25 OH D",
    "TSH FT3 FT4",
    "TORCH - TOXO IGM IGG",
    "RUBELLA IGM IGG",
    "CMV IGM IGG",
    "HSV",
    "SIFILIZ QUICK TEST OSE IGM",
    "PARVOVIRUS B19 IGM IGG",
    "EBV IGM IGG",
    "VZV IGM IGG",
    "HEP B HBSAG",
    "HCV IGM",
    "HIV AG/AB COMBI",
    "ELEKTROFOREZA E HB",
    "APTT, INR",
    "FACTOR V LEIDEN",
    "KULTURE VAGINALE (GRAM MYK UREAPLASMA MYCOPLMA",
    "AF-GENITAL +KLAMIDIA",
    "SST",
    "HPV",
    "PAP TEST",
    "EKO OBSTETRIKALE +NT (TRANSLUCENCA NUKALE)",
    "EKO MORFOLOGJIKE MINOR JAVA 11-14",
    "BI TEST OSE NIPT",
    "EKO OBSTETRIKALE JAVA 11-14 ME MJEK TE HUAJ",
    "FIBROZA CISTIKE",
    "DUCHENNE &BECKER MUSKULAR DYSTROPHIES",
    "EGZAMINIME TE TJERA",
];

export interface ExaminationFormValues {
    // Pregnancy Section
    id?: number,
    week?: string;
    lastPeriod?: Dayjs | null;
    periodLength?: number;
    dueDate?: Dayjs | null;
    estimationMethod?: string;
    // Medical Data Section
    preWeight?: number;
    currentWeight?: number;
    height?: number;
    bmi?: number;
    bp?: string;
    pulse?: number;
    fmf?: string;
    // Medical History Section
    vitae?: string;
    family?: string;
    gyn?: string;
    // Examination Section
    method?: string;
    conditions?: string;
    pregnancy?: string;
    // Measurements Section - Ultrasound diagnosis
    fetalPosition?: string;
    placentalLocation?: string;
    umbilicalCord?: string;
    amountOfAmnioticFluid?: string;
    // Fetal measurements
    bpd?: string;
    hc?: string;
    ac?: string;
    fl?: string;
    // Diagnosis
    diagnosis?: string;
    // Recommendation
    recommendations?: string[];
}

interface Props {
    isEdit: boolean;
    isModalOpen: boolean;
    onCancel: () => void;
    patientId: string;
    examinationId?: number | null;
    onSuccess: () => void; // parent callback after successful save
}

const ObstetricExaminationModal = ({
    isEdit,
    isModalOpen: open,
    onCancel,
    patientId,
    examinationId: editId,
    onSuccess,
}: Props) => {
    const isMobile = useIsMobile();
    const [analyses, setAnalyses] = useState(initialAnalyses);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm<ExaminationFormValues>();
    const contentRef = useRef<HTMLDivElement | null>(null);
    const horizontalPadding = isMobile ? 12 : 24;
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewData, setPreviewData] = useState<ExaminationFormValues>({});
    const [compareVisible, setCompareVisible] = useState(false);

    const handleEdit = async (examiantion: Examinations) => {
        setLoading(true);
        try {
            const rp = await BaseService.get<ExaminationDetails>("/obstetric-examinations/details/", examiantion.id);
            if (rp.Status) {
                const data = rp.Data;
                form.setFieldsValue({
                    ...data,
                    lastPeriod: data.lastPeriod ? dayjs(data.lastPeriod, "DD.MM.YYYY") : null,
                    dueDate: data.dueDate ? dayjs(data.dueDate, "DD.MM.YYYY") : null,
                });
            } else {
                console.error("Stats error:", rp.Messages, rp.Exception);
            }
        } finally {
            setLoading(false);

        }
    };

    useEffect(() => {
        if (open) {
            requestAnimationFrame(() => {
                setTimeout(() => {
                    if (contentRef.current) {
                        contentRef.current.scrollTop = 0;
                    } else {
                        const modalBody = document.querySelector(".ant-modal-body") as HTMLElement | null;
                        if (modalBody) modalBody.scrollTop = 0;
                    }
                }, 0);
            });
        }
    }, [open]);

    useEffect(() => {
        if (open && isEdit && editId) {
            handleEdit({ id: editId } as Examinations);
        } else if (open && !isEdit) {
            form.resetFields();
        }
    }, [open, isEdit, editId]);

    const handleChange = (selectedValues: string[]) => {
        const remainingOptions = initialAnalyses.filter(
            (item) => !selectedValues.includes(item)
        );
        setAnalyses(remainingOptions);
    };

  const openCompare = async () => setCompareVisible(true);

    const openPreview = async () => {
        try {
            const values = await form.validateFields();
            setPreviewData(values); setPreviewVisible(true);
        }
        catch {
            const allValues = form.getFieldsValue(true);
            setPreviewData(allValues);
            setPreviewVisible(true);
        }
    };

    // 🔹 Handle Add or Edit inside the modal
    const handleSubmit = async (values: ExaminationFormValues) => {
        setLoading(true);
        const payload = {
            ...values,
            lastPeriod: values.lastPeriod ? values.lastPeriod.format("DD.MM.YYYY") : null,
            dueDate: values.dueDate ? values.dueDate.format("DD.MM.YYYY") : null,
        };
        try {
            let rp;
            if (isEdit && values.id) {
                rp = await BaseService.update("/obstetric-examinations/", values.id, payload);
            } else {
                rp = await BaseService.create(`/obstetric-examinations/${patientId}`, payload);
            }
            if (rp.Status) {
                notify({
                    title: "Success",
                    description: isEdit
                        ? "Examination has been updated."
                        : "Examination has been added.",
                    variant: "success",
                });
                onCancel();
                onSuccess();
            } else {
                notify({
                    title: "Error",
                    description: "Failed to save Examination.",
                    variant: "error",
                });
                console.error("Messages:", rp.Messages, "Exception:", rp.Exception);
            }
        } finally {
            setLoading(false);
        }
    };

    const onValuesChange = (_changedValues: Partial<ExaminationFormValues>, allValues: ExaminationFormValues) => {
        // Calculate BMI
        const { currentWeight, height } = allValues;
        const newBmi = calculateBMI(currentWeight, height);
        const currentBmi = form.getFieldValue("bmi");
        if (newBmi !== currentBmi && newBmi !== null) {
            form.setFieldsValue({ bmi: newBmi });
        }
        // Calculate Estimated Due Date if lastPeriod is set
        const lastPeriod = form.getFieldValue("lastPeriod");
        const periodLength = form.getFieldValue("periodLength");
        if (lastPeriod) {
            const edd = calculateEstimatedDueDate(lastPeriod, periodLength);
            // Only update if changed to avoid loops
            const currentEdd = form.getFieldValue("dueDate");
            if (!currentEdd || !currentEdd.isSame(edd, "day")) {
                form.setFieldsValue({ dueDate: edd });
            }
        }
    };

    const calculateBMI = (weight?: number, height?: number) => {
        if (weight && height) {
            const heightInMeters = height / 100;
            if (heightInMeters === 0) return null;
            return parseFloat((weight / (heightInMeters * heightInMeters)).toFixed(1));
        }
        return null;
    };

    const calculateEstimatedDueDate = (lastPeriod?: Dayjs, periodLength?: number) => {
        const cycleLength = periodLength ?? 28; // default 28 days if not set
        // Naegele's Rule + adjustment
        return (lastPeriod as Dayjs).add(280 + (cycleLength - 28), "day");
    };

    useEffect(() => {
        if (!open) {
            setAnalyses(initialAnalyses);
        }
    }, [open]);

    return (
        <div>
            <Modal
                title={
                    <div
                        style={{
                            textAlign: "center",
                            fontWeight: 600,
                            fontSize: "1.5rem",
                            color: "#1f1f1f",
                            borderBottom: "1px solid #f0f0f0",
                            paddingBottom: 12,
                        }}
                    >
                        {isEdit ? (
                            <>
                                <BabyIcon style={{ color: "#eb2f96", marginRight: 8 }} />
                                Edit Obstetric Examination
                            </>
                        ) : (
                            <>
                                <BabyIcon style={{ color: "#eb2f96", marginRight: 8 }} />
                                Add Obstetric Examination
                            </>
                        )}
                        <div
                            style={{
                                fontSize: 14,
                                fontWeight: 400,
                                color: "#6b7280",
                                marginTop: 4,
                            }}
                        >
                            {isEdit
                                ? "Update the obstetric examination details below"
                                : "Fill in the details to add a new obstetric examination"}
                        </div>
                    </div>
                }
                centered
                open={open}
                footer={null}
                width={isMobile ? "calc(100vw - 24px)" : "60vw"}
                confirmLoading={loading}
                onCancel={onCancel}
                styles={{
                    body: { maxHeight: isMobile ? "75vh" : "80vh", overflowY: "auto", position: "relative" },
                }}

            >
                {loading && (
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            background: "rgba(255,255,255,0.7)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            zIndex: 1000,
                        }}
                    >
                        <Spin size="large" />
                    </div>
                )}
                <div style={{ maxHeight: '65vh', overflowY: 'auto', paddingRight: 8 }}>
                    <Form <ExaminationFormValues>
                        form={form}
                        name="validateOnly"
                        layout="vertical"
                        autoComplete="off"
                        onFinish={handleSubmit}
                        initialValues={{
                            examinationDate: dayjs(),
                        }}
                        disabled={loading}
                        onValuesChange={onValuesChange}
                        style={{
                            padding: isEdit
                                ? `16px ${horizontalPadding}px 0`
                                : `0px ${horizontalPadding}px 0`,
                        }}
                    >
                        {isEdit && (
                            <Form.Item
                                name="id"
                                label="Examination ID"
                                rules={[{ required: true }]}
                            >
                                <Input disabled />
                            </Form.Item>
                        )}
                        {/* Pregnancy Section */}
                        <Title level={4}>Pregnancy</Title>
                        <Form.Item label="Week" name="week">
                            <Input placeholder="36 + 0" />
                        </Form.Item>
                        <Form.Item label="Last period" name="lastPeriod">
                            <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
                        </Form.Item>
                        <Form.Item label="Period length" name="periodLength">
                            <InputNumber style={{ width: "100%" }} placeholder="28" />
                        </Form.Item>
                        <Form.Item label="Estimated due date" name="dueDate">
                            <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
                        </Form.Item>
                        <Form.Item label="Estimation method" name="estimationMethod">
                            <Input placeholder="Last Period" />
                        </Form.Item>

                        <Divider />

                        {/* Medical Data Section */}
                        <Title level={4}>Medical data</Title>
                        <Form.Item label="Mother's weight before pregnancy (kg)" name="preWeight">
                            <InputNumber style={{ width: "100%" }} placeholder="70" />
                        </Form.Item>
                        <Form.Item label="Mother's weight (kg)" name="currentWeight">
                            <InputNumber style={{ width: "100%" }} placeholder="85" />
                        </Form.Item>
                        <Form.Item label="Mother's height (cm)" name="height">
                            <InputNumber style={{ width: "100%" }} placeholder="162" />
                        </Form.Item>
                        <Form.Item label="BMI" name="bmi">
                            <InputNumber style={{ width: "100%" }} placeholder="43.8" />
                        </Form.Item>
                        <Form.Item label="Arterial Pressure" name="bp">
                            <Input placeholder="123/79" />
                        </Form.Item>
                        <Form.Item label="Pulse rate (min)" name="pulse">
                            <InputNumber style={{ width: "100%" }} placeholder="89" />
                        </Form.Item>
                        <Form.Item label="FMF - First trimester screening" name="fmf">
                            <Input placeholder="NIPt Risk i ulet" />
                        </Form.Item>

                        <Divider />

                        {/* Medical history Section */}
                        <Title level={4}>Medical history</Title>
                        <Form.Item label="Vitae" name="vitae">
                            <Input.TextArea rows={4} />
                        </Form.Item>
                        <Form.Item label="Family" name="family">
                            <Input.TextArea rows={4} />
                        </Form.Item>
                        <Form.Item label="Gyn" name="gyn">
                            <Input.TextArea rows={4} />
                        </Form.Item>

                        <Divider />

                        {/* Examination Section */}
                        <Title level={4}>Examination</Title>
                        <Form.Item label="Method" name="method">
                            <Input placeholder="Transabdominal scan" />
                        </Form.Item>
                        <Form.Item label="Conditions" name="conditions">
                            <Input placeholder="Good" />
                        </Form.Item>
                        <Form.Item label="Pregnancy" name="pregnancy">
                            <Input placeholder="Singleton pregnancy" />
                        </Form.Item>

                        <Divider />

                        {/* Examination Section */}
                        <Title level={4}>Mesurements</Title>
                        <Title level={5}>Ultrasound diagnosis</Title>
                        <Form.Item label="Fetal position" name="fetalPosition">
                            <Input placeholder="Cephalic position" />
                        </Form.Item>
                        <Form.Item label="Placental location" name="placentalLocation">
                            <Input placeholder="High anterior placenta" />
                        </Form.Item>
                        <Form.Item label="Umbilical cord" name="umbilicalCord">
                            <Input placeholder="Singleton pregnancy" />
                        </Form.Item>
                        <Form.Item label="Amount of amniotic fluid" name="amountOfAmnioticFluid">
                            <Input placeholder="Normal" />
                        </Form.Item>

                        <Title level={5}>Fetal measurements</Title>
                        <Form.Item label="Biparietal diameter (BPD)" name="bpd">
                            <Input placeholder="10 cm / 41 +1" />
                        </Form.Item>
                        <Form.Item label="Head circumference (HC)" name="hc">
                            <Input placeholder="35.19 cm / 41 + 0" />
                        </Form.Item>
                        <Form.Item label="Abdominal circumference (AC)" name="ac">
                            <Input placeholder="33,74 cm / 37 + 5" />
                        </Form.Item>
                        <Form.Item label="Femur (FL)" name="fl">
                            <Input placeholder="7.53 cm / 38 + 4" />
                        </Form.Item>

                        <Divider />

                        {/* Measurements Section */}
                        <Title level={4}>Diagnosis</Title>
                        <Form.Item label="Diagnosis" name="diagnosis">
                            <Input.TextArea rows={4} placeholder="Diagnosis" />
                        </Form.Item>

                        <Divider />

                        {/* Recommendation Section */}
                        <Title level={4}>Recommendation</Title>
                        <Form.Item label="Recommendation" name="recommendations">
                            <Select
                                mode="tags"
                                style={{ width: "100%" }}
                                placeholder="Select or type recommendations"
                                onChange={handleChange}
                                tokenSeparators={[',']}
                            >
                                {analyses.map((analysis) => (
                                    <Option key={analysis} value={analysis}>
                                        {analysis}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Form>
                </div>
                <div
                    style={{
                        position: "sticky",
                        bottom: 0,
                        background: "#fff",
                        padding: "20px 28px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 14,
                        borderTop: "1px solid #f0f0f0",
                        boxShadow: "0 -4px 12px rgba(0, 0, 0, 0.06)",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 16,
                        }}
                    >
                        <div style={{ display: "flex", gap: 10 }}>
                            <Tooltip title="Compare with previous examinations">
                                <Button icon={<DiffOutlined />} onClick={openCompare}>
                                    Compare
                                </Button>
                            </Tooltip>
                            <Tooltip title="Preview the examination before saving">
                                <Button icon={<EyeOutlined />} onClick={openPreview}>
                                    Preview
                                </Button>
                            </Tooltip>
                        </div>
                        <div style={{ display: "flex", gap: 10 }}>
                            <Button danger icon={<CloseOutlined />} onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={() => form.submit()}
                                loading={loading}
                            >
                                {isEdit ? "Update" : "Save"}
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>
            {/* Preview Modal */}
            <ObstetricExaminationPreviewModal
                open={previewVisible}
                isEdit={isEdit}
                onClose={() => setPreviewVisible(false)}
                previewData={previewData}
            />
            <ObstetricComparisonModal
                open={compareVisible}
                onClose={() => setCompareVisible(false)}
                patientId={patientId}
                formValues={form.getFieldsValue(true)}
            />
        </div>
    );
};

export default ObstetricExaminationModal;
